import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';

const TicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters - arrays for multi-select pills
  const [statusFilters, setStatusFilters] = useState(['open', 'in_progress', 'resolved']);
  const [priorityFilters, setPriorityFilters] = useState([]);
  const [categoryFilters, setCategoryFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // View states
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [statusFilters, priorityFilters, categoryFilters, searchQuery, page]);

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit,
        ...(statusFilters.length > 0 && { status: statusFilters.join(',') }),
        ...(priorityFilters.length > 0 && { priority: priorityFilters.join(',') }),
        ...(categoryFilters.length > 0 && { category: categoryFilters.join(',') }),
        ...(searchQuery && { search: searchQuery })
      };

      const response = await axios.get(`${API}/super-admin/tickets`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setTickets(response.data.tickets || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      setError(err.response?.data?.detail || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/tickets/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-blue-100 text-blue-800 border-blue-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      high: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-800 border-green-200',
      in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      resolved: 'bg-purple-100 text-purple-800 border-purple-200',
      closed: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };
  const handleQuickStatusChange = async (e, ticketId, newStatus) => {
    e.stopPropagation(); // Prevent opening the ticket detail modal
    try {
      await axios.put(
        `${API}/super-admin/tickets/${ticketId}/status-with-notification?new_status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      fetchTickets();
      fetchStats();
    } catch (err) {
      console.error('Failed to update status:', err);
      const errorMsg = err.response?.data?.detail;
      setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to update status');
    }
  };

  const formatCategoryName = (category) => {
    const names = {
      account_issue: 'Account Issue',
      data_issue: 'Data Issue',
      how_to: 'How-To Question',
      performance: 'Performance Issue',
      other: 'Other'
    };
    return names[category] || category;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !tickets.length) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading tickets...</span>
      </div>
    );
  }

  const mainContent = (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Support Tickets</h1>
              <p className="text-purple-200 text-sm">Manage customer support requests and inquiries</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-2.5 bg-white text-purple-900 rounded-lg font-semibold hover:bg-purple-50 transition-all shadow-lg"
          >
            + Create Ticket
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="text-sm text-gray-600 font-medium mb-1">Total Tickets</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total || 0}</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border border-green-200 p-6">
            <div className="text-sm text-green-700 font-medium mb-1">Open</div>
            <div className="text-3xl font-bold text-green-600">{stats.open || 0}</div>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl shadow-sm border border-yellow-200 p-6">
            <div className="text-sm text-yellow-700 font-medium mb-1">In Progress</div>
            <div className="text-3xl font-bold text-yellow-600">{stats.in_progress || 0}</div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm border border-purple-200 p-6">
            <div className="text-sm text-purple-700 font-medium mb-1">Resolved</div>
            <div className="text-3xl font-bold text-purple-600">{stats.resolved || 0}</div>
          </div>
        </div>
      )}

      {/* Filters - Pill Selectors */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Filters</span>
          {(statusFilters.length > 0 || priorityFilters.length > 0 || categoryFilters.length > 0 || searchQuery) && (
            <button
              onClick={() => {
                setStatusFilters([]);
                setPriorityFilters([]);
                setCategoryFilters([]);
                setSearchQuery('');
                setPage(1);
              }}
              className="text-xs text-purple-600 hover:text-purple-700 font-medium"
            >
              Clear all
            </button>
          )}
        </div>
        
        {/* Row 1: Status + Priority + Category Pills */}
        <div className="flex flex-wrap items-start gap-6">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Status</label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'open', label: 'Open', color: 'green' },
                { value: 'in_progress', label: 'In Progress', color: 'yellow' },
                { value: 'resolved', label: 'Resolved', color: 'purple' },
                { value: 'closed', label: 'Closed', color: 'gray' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    if (statusFilters.includes(option.value)) {
                      setStatusFilters(statusFilters.filter(s => s !== option.value));
                    } else {
                      setStatusFilters([...statusFilters, option.value]);
                    }
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    statusFilters.includes(option.value)
                      ? option.color === 'green' ? 'bg-green-100 text-green-800 border-green-300 ring-2 ring-green-500 ring-offset-1'
                      : option.color === 'yellow' ? 'bg-yellow-100 text-yellow-800 border-yellow-300 ring-2 ring-yellow-500 ring-offset-1'
                      : option.color === 'purple' ? 'bg-purple-100 text-purple-800 border-purple-300 ring-2 ring-purple-500 ring-offset-1'
                      : 'bg-gray-100 text-gray-800 border-gray-300 ring-2 ring-gray-500 ring-offset-1'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border-l border-gray-200 pl-6">
            <label className="block text-xs font-medium text-gray-500 mb-2">Priority</label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'low', label: 'Low', color: 'blue' },
                { value: 'medium', label: 'Medium', color: 'yellow' },
                { value: 'high', label: 'High', color: 'red' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    if (priorityFilters.includes(option.value)) {
                      setPriorityFilters(priorityFilters.filter(p => p !== option.value));
                    } else {
                      setPriorityFilters([...priorityFilters, option.value]);
                    }
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    priorityFilters.includes(option.value)
                      ? option.color === 'blue' ? 'bg-blue-100 text-blue-800 border-blue-300 ring-2 ring-blue-500 ring-offset-1'
                      : option.color === 'yellow' ? 'bg-yellow-100 text-yellow-800 border-yellow-300 ring-2 ring-yellow-500 ring-offset-1'
                      : 'bg-red-100 text-red-800 border-red-300 ring-2 ring-red-500 ring-offset-1'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border-l border-gray-200 pl-6">
            <label className="block text-xs font-medium text-gray-500 mb-2">Category</label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'account_issue', label: 'Account Issue' },
                { value: 'data_issue', label: 'Data Issue' },
                { value: 'how_to', label: 'How-To Question' },
                { value: 'performance', label: 'Performance Issue' },
                { value: 'other', label: 'Other' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    if (categoryFilters.includes(option.value)) {
                      setCategoryFilters(categoryFilters.filter(c => c !== option.value));
                    } else {
                      setCategoryFilters([...categoryFilters, option.value]);
                    }
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    categoryFilters.includes(option.value)
                      ? 'bg-purple-100 text-purple-800 border-purple-300 ring-2 ring-purple-500 ring-offset-1'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2: Search */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Search</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            placeholder="Search by ID, subject, description, or email..."
            className="w-full max-w-md px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button onClick={fetchTickets} className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
            Retry
          </button>
        </div>
      )}

      {/* Tickets List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            Tickets {total > 0 && `(${total})`}
          </h2>
          <button
            onClick={() => { setPage(1); fetchTickets(); }}
            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            ↻ Refresh
          </button>
        </div>

        {tickets.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500 text-lg">No tickets found</p>
            <p className="text-gray-400 text-sm mt-1">
              {statusFilters.length > 0 || priorityFilters.length > 0 || categoryFilters.length > 0 || searchQuery
                ? 'Try adjusting your filters'
                : 'Create your first ticket to get started'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-bold text-purple-600">{ticket.id}</span>
                    <h3 className="text-base font-semibold text-gray-900">{ticket.subject}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority.toUpperCase()}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(ticket.status)}`}>
                      {ticket.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                  <span>📧 {ticket.submitted_by_email}</span>
                  <span>📁 {formatCategoryName(ticket.category)}</span>
                  <span>🕐 {formatDateTime(ticket.created_at)}</span>
                </div>
                
                <p className="text-sm text-gray-700 line-clamp-2">{ticket.description}</p>
                
                {((ticket.conversation?.length || 0) + (ticket.notes?.length || 0)) > 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    💬 {(ticket.conversation?.length || 0) + (ticket.notes?.length || 0)} {((ticket.conversation?.length || 0) + (ticket.notes?.length || 0)) === 1 ? 'message' : 'messages'}
                  </div>
                )}
                
                {/* Quick Action Button */}
                <div className="mt-3 flex justify-end">
                  {ticket.status === 'closed' ? (
                    <button
                      onClick={(e) => handleQuickStatusChange(e, ticket.id, 'resolved')}
                      className="px-3 py-1.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors flex items-center gap-1"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Reopen
                    </button>
                  ) : (
                    <button
                      onClick={(e) => handleQuickStatusChange(e, ticket.id, 'closed')}
                      className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Close
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
            <div className="text-sm text-gray-600">
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} tickets
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page * limit >= total}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <CreateTicketModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchTickets();
            fetchStats();
          }}
        />
      )}

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <TicketDetailsModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdate={() => {
            setSelectedTicket(null);
            fetchTickets();
            fetchStats();
          }}
        />
      )}
    </div>
  );

  return mainContent;
};

// Create Ticket Modal Component
const CreateTicketModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    customer_email: '',
    customer_name: '',
    category: 'other',
    priority: 'medium'
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API}/super-admin/tickets`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      onSuccess();
    } catch (err) {
      console.error('Failed to create ticket:', err);
      setError(err.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleOverlayClick = (e) => {
    // Backdrop click disabled - modal must be closed via buttons
  };

  return (
    <div onClick={handleOverlayClick} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Create New Support Ticket</h2>
              <p className="text-purple-100 text-sm mt-1">Submit a new customer support request</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-red-800">Error</p>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              maxLength={500}
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
              placeholder="Brief description of the issue"
            />
            <p className="text-xs text-gray-500 mt-1">{formData.subject.length}/500 characters</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Customer Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                placeholder="customer@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-white"
              >
                <option value="account_issue">Account Issue</option>
                <option value="data_issue">Data Issue</option>
                <option value="how_to">How-To Question</option>
                <option value="performance">Performance Issue</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Priority <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-white"
              >
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              maxLength={10000}
              rows={6}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all resize-none"
              placeholder="Detailed description of the issue..."
            />
            <p className="text-xs text-gray-500 mt-1">{formData.description.length}/10000 characters</p>
          </div>
        </form>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-white font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </span>
            ) : '✓ Create Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Ticket Details Modal Component
const TicketDetailsModal = ({ ticket: initialTicket, onClose, onUpdate }) => {
  const [ticket, setTicket] = useState(initialTicket);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(initialTicket.status);
  const [showStatusNote, setShowStatusNote] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [error, setError] = useState(null);

  const fetchTicketDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTicket(response.data);
      setSelectedStatus(response.data.status);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch ticket details:', err);
      setError('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (selectedStatus === ticket.status) return;
    
    // Show inline note input instead of browser prompt
    setShowStatusNote(true);
  };
  
  const confirmStatusUpdate = async () => {
    try {
      const noteParam = statusNote ? `&note=${encodeURIComponent(statusNote)}` : '';
      await axios.put(
        `${API}/super-admin/tickets/${ticket.id}/status-with-notification?new_status=${selectedStatus}${noteParam}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setShowStatusNote(false);
      setStatusNote('');
      setError(null);
      await fetchTicketDetails();
    } catch (err) {
      console.error('Failed to update status:', err);
      setError(err.response?.data?.detail || 'Failed to update status');
      setSelectedStatus(ticket.status);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;

    try {
      await axios.post(
        `${API}/super-admin/tickets/${ticket.id}/reply`,
        { message: noteText, is_internal: isInternalNote },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setNoteText('');
      setIsInternalNote(false);
      setError(null);
      await fetchTicketDetails();
    } catch (err) {
      console.error('Failed to add reply:', err);
      setError(err.response?.data?.detail || 'Failed to add reply');
    }
  };
  
  // Image modal state for this component
  const [imageModalUrl, setImageModalUrl] = useState(null);
  
  const handleOverlayClick = (e) => {
    // Backdrop click disabled - modal must be closed via buttons
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-blue-100 text-blue-800 border-blue-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      high: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-800 border-green-200',
      in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      resolved: 'bg-purple-100 text-purple-800 border-purple-200',
      closed: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatCategoryName = (category) => {
    const names = {
      account_issue: 'Account Issue',
      data_issue: 'Data Issue',
      how_to: 'How-To Question',
      performance: 'Performance Issue',
      other: 'Other'
    };
    return names[category] || category;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div onClick={handleOverlayClick} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold font-mono">{ticket.id}</h2>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white border border-white/30`}>
                {ticket.priority.toUpperCase()} PRIORITY
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <h3 className="text-xl font-medium">{ticket.subject}</h3>
          <p className="text-purple-100 text-sm mt-2">
            From: {ticket.submitted_by_name} ({ticket.submitted_by_email})
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Ticket Info */}
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2 px-2 py-1 bg-purple-100 rounded inline-block">Submitted By</p>
                <p className="text-base font-semibold text-gray-900 mt-1">{ticket.submitted_by_name}</p>
                <p className="text-sm text-gray-600">{ticket.submitted_by_email}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2 px-2 py-1 bg-blue-100 rounded inline-block">Category</p>
                <p className="text-base font-semibold text-gray-900 mt-1">{formatCategoryName(ticket.category)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2 px-2 py-1 bg-green-100 rounded inline-block">Created</p>
                <p className="text-base font-semibold text-gray-900 mt-1">{formatDateTime(ticket.created_at)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2 px-2 py-1 bg-indigo-100 rounded inline-block">Last Updated</p>
                <p className="text-base font-semibold text-gray-900 mt-1">{formatDateTime(ticket.updated_at)}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Description</p>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-gray-800 whitespace-pre-wrap">{ticket.description}</p>
            </div>
          </div>

          {/* Attachments */}
          {ticket.attachments && ticket.attachments.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                📎 Attachments ({ticket.attachments.length})
              </p>
              <div className="flex flex-wrap gap-3">
                {ticket.attachments.map((url, idx) => {
                  const fullUrl = url.startsWith('http') ? url : `${API.replace('/api', '')}${url}`;
                  return (
                    <button
                      key={idx}
                      onClick={() => setImageModalUrl(fullUrl)}
                      className="block focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
                    >
                      <img
                        src={fullUrl}
                        alt={`Attachment ${idx + 1}`}
                        className="h-24 w-24 object-cover rounded-lg border border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Status Update */}
          <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Update Ticket Status</h3>
            <div className="flex items-center gap-3 mb-3">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white font-medium"
              >
                <option value="open">🟢 Open</option>
                <option value="in_progress">🟡 In Progress</option>
                <option value="resolved">🟣 Resolved</option>
                <option value="closed">⚪ Closed</option>
              </select>
              <button
                onClick={handleStatusUpdate}
                disabled={selectedStatus === ticket.status}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
              >
                Update
              </button>
            </div>
            
            {/* Inline status note input */}
            {showStatusNote && (
              <div className="bg-white rounded-lg p-4 border border-purple-300 space-y-3">
                <label className="block text-sm font-semibold text-gray-900">
                  Add a note about this status change (optional)
                </label>
                <textarea
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Explain why the status is changing..."
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { setShowStatusNote(false); setStatusNote(''); setSelectedStatus(ticket.status); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmStatusUpdate}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-semibold"
                  >
                    Confirm Update
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Conversation Timeline */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">
              Conversation ({((ticket.conversation?.length || 0) + (ticket.notes?.length || 0))} messages)
            </p>
            
            {/* Merge and sort conversation + notes for display */}
            {(() => {
              const allMessages = [
                ...(ticket.conversation || []).map(msg => ({
                  id: msg.id,
                  author_name: msg.author_name,
                  message: msg.message,
                  is_internal: msg.is_internal || false,
                  is_from_user: msg.author_type === 'user',
                  is_status_change: msg.type === 'status_change',
                  email_sent: msg.email_sent || false,
                  attachments: msg.attachments || [],
                  created_at: msg.created_at
                })),
                ...(ticket.notes || []).map(note => ({
                  id: note.id,
                  author_name: note.added_by_name,
                  message: note.text,
                  is_internal: note.is_internal || false,
                  is_from_email: note.is_from_email || false,
                  is_from_user: false,
                  is_status_change: false,
                  email_sent: note.email_sent || false,
                  attachments: [],
                  created_at: note.created_at
                }))
              ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

              if (allMessages.length === 0) {
                return (
                  <div className="text-center py-6 text-gray-500 text-sm mb-4">
                    No messages yet. Add the first response below.
                  </div>
                );
              }

              return (
                <div className="space-y-3 mb-4">
                  {allMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`rounded-lg p-4 border ${
                        msg.is_from_user
                          ? 'bg-blue-50 border-blue-200'
                          : msg.is_internal
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">
                            {msg.is_from_user ? '👤' : msg.is_from_email ? '📧' : '💬'} {msg.author_name}
                          </span>
                          {msg.is_from_user && (
                            <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full font-semibold">
                              User
                            </span>
                          )}
                          {msg.is_internal && (
                            <span className="px-2 py-0.5 bg-yellow-600 text-white text-xs rounded-full font-semibold">
                              Internal Only
                            </span>
                          )}
                          {msg.is_from_email && (
                            <span className="px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full font-semibold">
                              Via Email
                            </span>
                          )}
                          {!msg.is_from_user && !msg.is_internal && msg.email_sent && (
                            <span
                              className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full font-semibold inline-flex items-center gap-1"
                              title="The customer received this as an email"
                              data-testid={`ticket-msg-emailed-badge-${msg.id}`}
                            >
                              ✉ Emailed
                            </span>
                          )}
                          {!msg.is_from_user && !msg.is_internal && !msg.is_status_change && !msg.email_sent && (
                            <span
                              className="px-2 py-0.5 bg-gray-300 text-gray-700 text-xs rounded-full font-semibold inline-flex items-center gap-1"
                              title="No email was sent (e.g. customer email missing or send failed)"
                              data-testid={`ticket-msg-not-emailed-badge-${msg.id}`}
                            >
                              ✉ Not emailed
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">{formatDateTime(msg.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{msg.message}</p>
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {msg.attachments.map((url, idx) => {
                            const fullUrl = url.startsWith('http') ? url : `${API.replace('/api', '')}${url}`;
                            return (
                              <button
                                key={idx}
                                onClick={() => setImageModalUrl(fullUrl)}
                                className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                              >
                                <img
                                  src={fullUrl}
                                  alt={`Attachment ${idx + 1}`}
                                  className="h-16 w-16 object-cover rounded border border-gray-300 hover:border-blue-500 cursor-pointer"
                                />
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Add Reply Form */}
            <form onSubmit={handleAddNote} className="border-t border-gray-200 pt-4">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={3}
                placeholder="Add a note or response..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-3"
                data-testid="ticket-reply-textarea"
              />

              {/* Reply Visibility Toggle (Public / Internal) */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex flex-col gap-1.5">
                  <div
                    role="radiogroup"
                    aria-label="Reply visibility"
                    className="inline-flex items-center p-1 bg-gray-100 rounded-full border border-gray-200"
                    data-testid="ticket-reply-visibility-toggle"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!isInternalNote}
                      onClick={() => setIsInternalNote(false)}
                      className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all flex items-center gap-1.5 ${
                        !isInternalNote
                          ? 'bg-green-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      data-testid="ticket-reply-visibility-public"
                    >
                      <span aria-hidden="true">✉</span>
                      Public reply
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isInternalNote}
                      onClick={() => setIsInternalNote(true)}
                      className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all flex items-center gap-1.5 ${
                        isInternalNote
                          ? 'bg-yellow-500 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      data-testid="ticket-reply-visibility-internal"
                    >
                      <span aria-hidden="true">🔒</span>
                      Internal note
                    </button>
                  </div>
                  <p
                    className="text-xs text-gray-500 pl-1"
                    data-testid="ticket-reply-visibility-hint"
                  >
                    {isInternalNote
                      ? 'Visible to admins only — the customer will not see this.'
                      : 'Customer will receive an email and a push notification.'}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={!noteText.trim()}
                  className={`px-6 py-2 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isInternalNote
                      ? 'bg-yellow-500 hover:bg-yellow-600'
                      : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                  data-testid="ticket-reply-submit"
                >
                  {isInternalNote ? 'Add Internal Note' : 'Send Response'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {/* Image Modal */}
      {imageModalUrl && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center p-4"
          onClick={() => setImageModalUrl(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setImageModalUrl(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-sm font-medium flex items-center gap-1"
            >
              <span>Close</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={imageModalUrl}
              alt="Attachment preview"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <a
              href={imageModalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 text-white hover:text-gray-300 text-sm font-medium flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span>Open in new tab</span>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
